import React, { useState } from 'react';
import { Alert, Spin, Statistic, Table, Typography } from 'antd';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useCurrency } from '@/contexts/CurrencyContext';
import {
  filtersQueryKey,
  getSalesAnalyticsInvoicing,
  type SalesAnalyticsFilterParams,
} from '@/services/salesAnalytics.service';
import { OiHorizontalBarChart } from '@/components/owner-intelligence/charts/OiCharts';
import { SalesAnalyticsToolbar } from './SalesAnalyticsToolbar';
import { SalesAnalyticsDrillDownDrawer } from './SalesAnalyticsDrillDownDrawer';

const { Title } = Typography;

export const SalesAnalyticsInvoicingTab: React.FC<{
  filters: SalesAnalyticsFilterParams;
  filterSummary: string;
}> = ({ filters, filterSummary }) => {
  const { formatCurrency } = useCurrency();
  const navigate = useNavigate();
  const [agingBucket, setAgingBucket] = useState<string | null>(null);

  const query = useQuery({
    queryKey: ['sales-analytics', 'invoicing', filtersQueryKey(filters)],
    queryFn: () => getSalesAnalyticsInvoicing(filters),
  });

  if (query.isLoading) return <Spin />;
  if (query.isError) return <Alert type="error" message={(query.error as Error).message} />;
  const d = query.data!;

  const printRows = d.invoices.map((r) => ({
    invoice_number: r.invoice_number,
    customer: r.customer_name,
    invoice_date: r.invoice_date,
    due_date: r.due_date,
    grand_total: formatCurrency(r.grand_total),
    balance_due: formatCurrency(r.balance_due),
  }));

  const drillRows =
    agingBucket != null
      ? d.invoices
          .filter((inv) => {
            if (agingBucket === '90+') return inv.balance_due > 0;
            return inv.balance_due > 0;
          })
          .slice(0, 25)
          .map((inv) => ({
            key: inv.invoice_id,
            label: inv.invoice_number,
            sublabel: inv.customer_name,
            date: inv.invoice_date,
            amount: inv.balance_due,
            link_path: inv.link_path,
          }))
      : [];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 12 }}>
        <Title level={5} style={{ margin: 0 }}>
          Invoicing &amp; AR
        </Title>
        <SalesAnalyticsToolbar
          tab="invoicing"
          tabTitle="Invoicing & AR"
          filters={filters}
          filterSummary={filterSummary}
          printColumns={[
            { key: 'invoice_number', label: 'Invoice' },
            { key: 'customer', label: 'Customer' },
            { key: 'invoice_date', label: 'Date' },
            { key: 'balance_due', label: 'Balance', align: 'right' },
          ]}
          printRows={printRows}
          printKpiLines={[{ label: 'Invoiced (period)', value: formatCurrency(d.invoiced_total) }]}
        />
      </div>

      <Statistic title="Invoiced in period" value={formatCurrency(d.invoiced_total)} suffix={`(${d.invoice_count} invoices)`} />

      <Title level={5} style={{ marginTop: 24 }}>
        AR aging (as of period end) — click bucket to drill down
      </Title>
      <div style={{ cursor: 'pointer' }}>
        <OiHorizontalBarChart
          data={d.ar_aging_buckets.map((b) => ({ name: b.bucket, value: b.amount }))}
          dataKey="value"
          nameKey="name"
          height={200}
        />
      </div>
      <Table
        size="small"
        rowKey="bucket"
        pagination={false}
        style={{ marginTop: 8 }}
        dataSource={d.ar_aging_buckets}
        onRow={(r) => ({ onClick: () => setAgingBucket(r.bucket), style: { cursor: 'pointer' } })}
        columns={[
          { title: 'Bucket', dataIndex: 'bucket' },
          { title: 'Count', dataIndex: 'count', align: 'right' },
          { title: 'Amount', dataIndex: 'amount', align: 'right', render: (v: number) => formatCurrency(v) },
        ]}
      />

      <Title level={5} style={{ marginTop: 24 }}>
        Overdue customers
      </Title>
      <Table
        size="small"
        rowKey="customer_id"
        dataSource={d.overdue_customers}
        pagination={false}
        columns={[
          { title: 'Customer', dataIndex: 'customer_name' },
          { title: 'Overdue', dataIndex: 'total_overdue', align: 'right', render: (v: number) => formatCurrency(v) },
          { title: 'Outstanding', dataIndex: 'total_outstanding', align: 'right', render: (v: number) => formatCurrency(v) },
          {
            title: '',
            key: 'stmt',
            width: 100,
            render: (_: unknown, r) => (
              <a onClick={() => navigate(`/fin/reports/customer-statement?customer_id=${r.customer_id}`)}>Statement</a>
            ),
          },
        ]}
      />

      <Title level={5} style={{ marginTop: 24 }}>
        Invoices in period
      </Title>
      <Table
        size="small"
        rowKey="invoice_id"
        dataSource={d.invoices}
        pagination={{ pageSize: 10 }}
        columns={[
          { title: 'Invoice', dataIndex: 'invoice_number' },
          { title: 'Customer', dataIndex: 'customer_name' },
          { title: 'Date', dataIndex: 'invoice_date', width: 110 },
          { title: 'Due', dataIndex: 'due_date', width: 110 },
          { title: 'Total', dataIndex: 'grand_total', align: 'right', render: (v: number) => formatCurrency(v) },
          { title: 'Balance', dataIndex: 'balance_due', align: 'right', render: (v: number) => formatCurrency(v) },
          { title: '', key: 'link', width: 80, render: (_: unknown, r) => <a onClick={() => navigate(r.link_path)}>View</a> },
        ]}
      />

      <SalesAnalyticsDrillDownDrawer
        open={Boolean(agingBucket)}
        onClose={() => setAgingBucket(null)}
        title={agingBucket ? `AR aging: ${agingBucket}` : ''}
        subtitle={filterSummary}
        rows={drillRows}
        formatCurrency={formatCurrency}
      />
    </div>
  );
};
